import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { NavTab } from '../models/nav-tab.interface';
import { 
  STUDENT_TABS, 
  TRAINER_TABS, 
  UserType, 
  TABS_BY_USER_TYPE 
} from '../models/tab-config';
import { StudentFeaturePreferencesService } from './student-feature-preferences.service';
import { TrainerFeaturePreferencesService } from './trainer-feature-preferences.service';
import { STUDENT_TAB_FEATURE_MAP } from '../models/student-feature-preferences';
import { TRAINER_TAB_FEATURE_MAP } from '../models/trainer-feature-preferences';

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  private activeTabSubject = new BehaviorSubject<string>('');
  public activeTab$ = this.activeTabSubject.asObservable();

  private currentUserType: UserType = UserType.GUEST;

  constructor(
    private readonly featurePrefs: StudentFeaturePreferencesService,
    private readonly trainerFeaturePrefs: TrainerFeaturePreferencesService
  ) {}

  /**
   * Define o tipo de usuário atual para determinar quais tabs exibir
   */
  setUserType(userType: UserType): void {
    this.currentUserType = userType;
  }

  /**
   * Retorna o tipo de usuário atual
   */
  getUserType(): UserType {
    return this.currentUserType;
  }

  /**
   * Retorna as tabs apropriadas baseadas no tipo de usuário atual
   */
  getTabsForCurrentUser(): NavTab[] {
    if (this.currentUserType === UserType.STUDENT) {
      return this.getStudentTabs();
    }
    if (this.currentUserType === UserType.TRAINER) {
      return this.getTrainerTabs();
    }
    return [...TABS_BY_USER_TYPE[this.currentUserType]];
  }

  /**
   * Retorna as tabs para um tipo de usuário específico
   */
  getTabsForUserType(userType: UserType): NavTab[] {
    if (userType === UserType.STUDENT) {
      return this.filterStudentTabs([...STUDENT_TABS]);
    }
    if (userType === UserType.TRAINER) {
      return this.filterTrainerTabs([...TRAINER_TABS]);
    }
    return [...TABS_BY_USER_TYPE[userType]];
  }

  /**
   * @deprecated Use getTabsForCurrentUser() instead
   */
  getDefaultTabs(): NavTab[] {
    return this.getTabsForCurrentUser();
  }

  /**
   * Retorna as tabs específicas do Student (filtradas pelas preferências)
   */
  getStudentTabs(): NavTab[] {
    return this.filterStudentTabs([...STUDENT_TABS]);
  }

  private filterStudentTabs(tabs: NavTab[]): NavTab[] {
    return tabs.filter((tab) => {
      const featureId = STUDENT_TAB_FEATURE_MAP[tab.id];
      if (!featureId) {
        return true; // Perfil e tabs sem mapeamento sempre visíveis
      }
      return this.featurePrefs.isEnabled(featureId);
    });
  }

  /**
   * Retorna as tabs específicas do Trainer (filtradas; Início e Ajustes sempre visíveis)
   */
  getTrainerTabs(): NavTab[] {
    return this.filterTrainerTabs([...TRAINER_TABS]);
  }

  private filterTrainerTabs(tabs: NavTab[]): NavTab[] {
    return tabs.filter((tab) => {
      const featureId = TRAINER_TAB_FEATURE_MAP[tab.id];
      if (!featureId) {
        return true;
      }
      return this.trainerFeaturePrefs.isEnabled(featureId);
    });
  }

  setActiveTab(tabId: string): void {
    this.activeTabSubject.next(tabId);
  }

  getActiveTab(): string {
    return this.activeTabSubject.value;
  }

  getActiveTabObservable(): Observable<string> {
    return this.activeTab$;
  }

  updateTabActiveState(tabs: NavTab[], currentRoute: string): NavTab[] {
    return tabs.map(tab => ({
      ...tab,
      isActive: tab.route === currentRoute
    }));
  }

  /**
   * Limpa o estado de navegação (útil para logout)
   */
  clearNavigationState(): void {
    this.currentUserType = UserType.GUEST;
    this.activeTabSubject.next('');
  }
}
